import ky, { type KyInstance } from 'ky';

let _csrfToken: string | null = null;
let _sessionFetchPromise: Promise<void> | null = null;

export function setCsrfToken(token: string | null): void {
  _csrfToken = token;
}

export const api: KyInstance = ky.create({
  prefixUrl: '/api',
  credentials: 'include',
  hooks: {
    beforeRequest: [
      (req) => {
        if (_csrfToken && !['GET', 'HEAD'].includes(req.method)) {
          req.headers.set('x-csrf-token', _csrfToken);
        }
      },
    ],
    // Tier-limit auto-toast for direct ky callers too. Mirrors the
    // logic in apiJson below - body is read once here so the original
    // HTTPError still has its body available downstream.
    afterResponse: [
      async (_req, _opts, res) => {
        if (res.status !== 403) return;
        const body = await res.clone().json().catch(() => null);
        if (body && typeof body === 'object' && (body as { error?: string }).error === 'tier_limit') {
          handleTierError({ body, status: 403 });
        }
      },
    ],
  },
  timeout: 30_000,
  retry: 0,
});

/**
 * Fetch the session endpoint and cache _csrfToken. Concurrent callers share one
 * inflight promise. Guarantees every non-GET request has a fresh CSRF token -
 * fixes the "Adding… hangs forever" class of bugs.
 */
async function ensureCsrfToken(): Promise<void> {
  if (_csrfToken) return;
  if (!_sessionFetchPromise) {
    _sessionFetchPromise = fetch('/api/auth/session', { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) return;
        const data = (await r.json()) as { csrfToken?: string | null };
        if (data.csrfToken) _csrfToken = data.csrfToken;
      })
      .catch(() => undefined)
      .finally(() => {
        _sessionFetchPromise = null;
      });
  }
  await _sessionFetchPromise;
}

import { handleTierError } from './tier-error';

/** Raw JSON helper that throws on non-2xx. */
export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const needsCsrf = !['GET', 'HEAD'].includes(method);
  if (needsCsrf) await ensureCsrfToken();

  const headers = new Headers(init.headers);
  if (!headers.has('content-type') && init.body !== undefined) {
    headers.set('content-type', 'application/json');
  }
  if (needsCsrf && _csrfToken) {
    headers.set('x-csrf-token', _csrfToken);
  }

  let res: Response;
  try {
    res = await fetch(`/api/${path.replace(/^\/+/, '')}`, {
      ...init,
      method,
      credentials: 'include',
      headers,
    });
  } catch (err) {
    throw Object.assign(new Error('Network error'), { cause: err, status: 0 });
  }

  // If CSRF was rejected (token rotated), refresh and retry once.
  if (res.status === 403 && needsCsrf) {
    _csrfToken = null;
    await ensureCsrfToken();
    if (_csrfToken) {
      headers.set('x-csrf-token', _csrfToken);
      res = await fetch(`/api/${path.replace(/^\/+/, '')}`, {
        ...init,
        method,
        credentials: 'include',
        headers,
      });
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'error' }));
    const msg =
      (body as { message?: string; error?: string }).message ??
      (body as { error?: string }).error ??
      'Request failed';
    const err = Object.assign(new Error(msg), { status: res.status, body });
    // Auto-toast tier-limit errors so every mutation caller doesn't need
    // its own onError handler to surface them. Re-thrown so query
    // invalidation / state rollback still happens.
    handleTierError(err);
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
